/**
 * `TimeZoneProvider` / `useDisplayTimeZone` (ADR-0160 Slice 2).
 *
 * Resolves the effective DISPLAY time zone for interactive dashboard dates:
 * the per-site override (`activeSite.display_time_zone`) wins, else the
 * account-wide default (`account.display_time_zone`), else `'UTC'`. The value
 * feeds `formatIsoDate(iso, tz)` at the call sites.
 *
 * DISPLAY-ONLY: this never affects stored, wired, or signed timestamps
 * (spec §3). The signed audit envelope, exports, and the security activity
 * trail stay UTC and do NOT read this context.
 *
 * The context default is `'UTC'`, so any component rendered WITHOUT this
 * provider (e.g. in a unit test) formats exactly as it did pre-ADR-0160.
 * The account default is fetched once per session; `refreshAccountTimeZone`
 * lets the Profile control (Slice 3) reflect a change without a reload.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { resolveDisplayTimeZone } from '@sill/types';

import { getAccount } from '../api/account.js';
import { useHttp } from '../api/http-context.js';
import { useSites } from './providers.js';

interface TimeZoneCtxValue {
  /** Effective display zone: site override ?? account default ?? 'UTC'. */
  readonly timeZone: string;
  /** The account-wide default alone ('UTC' when unset) — for the per-site
   *  override's "Inherit account default (…)" label. */
  readonly accountTimeZone: string;
  /** Re-fetch the account default (call after changing it in Profile). */
  readonly refreshAccountTimeZone: () => void;
}

const TimeZoneCtx = createContext<TimeZoneCtxValue>({
  timeZone: 'UTC',
  accountTimeZone: 'UTC',
  refreshAccountTimeZone: () => {},
});

/** The effective display time zone for the current site + account. */
export function useDisplayTimeZone(): string {
  return useContext(TimeZoneCtx).timeZone;
}

/** Full context — the zone plus the account-default refresh handle. */
export function useTimeZoneContext(): TimeZoneCtxValue {
  return useContext(TimeZoneCtx);
}

export function TimeZoneProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const http = useHttp();
  const { activeSite } = useSites();
  const [accountTz, setAccountTz] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const refreshAccountTimeZone = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void getAccount(http).then((result) => {
      if (cancelled) return;
      // Fail-soft: a fetch error just leaves the account default unset, so
      // the effective zone falls back to the site override or UTC.
      setAccountTz(result.kind === 'ok' ? result.account.display_time_zone ?? null : null);
    });
    return (): void => {
      cancelled = true;
    };
  }, [http, nonce]);

  const timeZone = resolveDisplayTimeZone(
    activeSite?.display_time_zone ?? null,
    accountTz,
  );
  const accountTimeZone = accountTz ?? 'UTC';

  return (
    <TimeZoneCtx.Provider
      value={{ timeZone, accountTimeZone, refreshAccountTimeZone }}
    >
      {children}
    </TimeZoneCtx.Provider>
  );
}
